/**
 * Phase 10 R5 — useDayLaborLines (BATCHED)
 *
 * Single-query day-scoped fetch of labor columns for every ticket scheduled
 * for `dateStr` in the active shop. Mirrors the per-ticket shape from
 * `useTicketLaborLines` but in one batched call.
 *
 * HARD CONTRACT: exactly **one** Supabase query per invocation.
 *
 * AUTHORITY (FF-A / FF-F):
 *   Reads from `public.v_ticket_jobs_labor` (SECURITY INVOKER view) —
 *   see useTicketLaborLines doc block for the masking contract. The
 *   view exposes `shop_id` and `scheduled_date` directly so the day
 *   filter is a flat `.eq(...)` pair, dropping the prior PostgREST
 *   inner-join embed on the parent tickets row.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { TicketLaborLine } from '@/hooks/labor/useTicketLaborLines';

export function useDayLaborLines(dateStr: string | undefined, enabled = true) {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  return useQuery<TicketLaborLine[]>({
    queryKey: ['day-labor-lines', shopId, dateStr],
    enabled: !!shopId && !!dateStr && enabled,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('v_ticket_jobs_labor')
        .select(
          `ticket_id, job_id, minutes_override,
           sold_minutes, labor_rate_applied, labor_rate_resolved,
           labor_rate_source, labor_rate_reason, labor_sell_amount,
           labor_rate_overridden,
           job_canonical_id, job_name, job_standard_minutes, job_category`,
        )
        .eq('shop_id', shopId)
        .eq('scheduled_date', dateStr)
        .is('deleted_at', null);
      if (error) throw error;

      return ((data ?? []) as any[]).map((r) => {
        const effective = r.minutes_override ?? r.job_standard_minutes ?? 0;
        const isUncomputed =
          r.labor_sell_amount == null &&
          r.labor_rate_applied == null &&
          r.sold_minutes == null;
        return {
          ticket_id: r.ticket_id,
          job_id: r.job_id,
          job_name: r.job_name ?? 'Unnamed job',
          job_category: r.job_category ?? null,
          effective_minutes: effective,
          sold_minutes: r.sold_minutes,
          labor_rate_applied:
            r.labor_rate_applied != null ? Number(r.labor_rate_applied) : null,
          labor_rate_resolved:
            r.labor_rate_resolved != null ? Number(r.labor_rate_resolved) : null,
          labor_rate_source: r.labor_rate_source,
          labor_rate_reason: r.labor_rate_reason,
          labor_sell_amount:
            r.labor_sell_amount != null ? Number(r.labor_sell_amount) : null,
          labor_rate_overridden: !!r.labor_rate_overridden,
          is_uncomputed: isUncomputed,
        } satisfies TicketLaborLine;
      });
    },
  });
}
